import * as React from 'react';
import {
  useDragAndDrop,
  useTreeData,
  Tree,
  TreeItem,
  TreeItemContent,
  Collection,
  DropIndicator,
  type Key,
} from 'react-aria-components';

import { ArticleNavItem } from '../types';
import { ArticleItem } from './article-item';
import { useSidebar } from '../context';
import { getNewlyExpandedKeys } from './expansion';
import { buildArticleTreeSyncOperations } from './tree-sync';

let placeholderCounter = 0;

interface ArticleTreeProps {
  articles: ArticleNavItem[];
  isSearching?: boolean;
  renderEmptyState?: () => React.ReactNode;
}

export function getParentKeys(items: ArticleNavItem[], targetId: string, parents: string[] = []): string[] | null {
  for (const item of items) {
    if (item.id === targetId) {
      return parents;
    }
    if (item.items && item.items.length > 0) {
      const result = getParentKeys(item.items, targetId, [...parents, item.id]);
      if (result) return result;
    }
  }

  return null;
}

type ArticleTreeNode = {
  value: ArticleNavItem;
  children: ArticleTreeNode[] | null;
};

function treeNodesToArticleNavItems(items: ArticleTreeNode[]): ArticleNavItem[] {
  return items.map((item) => ({
    ...item.value,
    items: item.children?.length ? treeNodesToArticleNavItems(item.children) : item.value.items,
  }));
}

export function ArticleTree({ articles, isSearching, renderEmptyState }: ArticleTreeProps) {
  const {
    ItemLinkComponent,
    activeArticleId,
    lastSelectedArticleId,
    setLastSelectedArticleId,
    onSelectArticle,
    onExpandArticle,
    handleSearchArticles,
    setSearchValue,
    canMoveArticle,
    onMoveArticle,
    onAddArticle,
    onMoreActions,
  } = useSidebar();

  const effectiveActiveId = activeArticleId ?? lastSelectedArticleId ?? undefined;

  const articlesTree = useTreeData({
    initialItems: articles,
    getKey: (articleItem) => articleItem.id,
    getChildren: (articleItem) => articleItem.items ?? [],
  });

  const prevArticlesRef = React.useRef(articles);
  React.useEffect(() => {
    if (prevArticlesRef.current === articles) return;

    prevArticlesRef.current = articles;

    const currentArticles = treeNodesToArticleNavItems(articlesTree.items);
    const operations = buildArticleTreeSyncOperations(currentArticles, articles);

    if (operations.length === 0) return;

    operations.forEach((operation) => {
      switch (operation.type) {
        case 'remove':
          articlesTree.remove(operation.id);
          break;
        case 'insert':
          articlesTree.insert(operation.parentId, operation.index, operation.item);
          break;
        case 'move':
          articlesTree.move(operation.id, operation.parentId, operation.index);
          break;
        case 'update':
          articlesTree.update(operation.id, operation.item);
          break;
      }
    });
  }, [articles, articlesTree]);

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (_keys, items: typeof articlesTree.items) =>
      items.map((item) => ({
        'text/plain': item.value.label,
      })),
    onMove: (e) => {
      if (isSearching) return;
      const movedArticleId = Array.from(e.keys)[0];
      if (!movedArticleId) return;

      const targetItem = articlesTree.getItem(e.target.key);
      if (!targetItem) return;

      let movedToParentId: string | null;
      let movedToIndex: number;

      if (e.target.dropPosition === 'on') {
        movedToParentId = e.target.key.toString();
        movedToIndex = targetItem.children?.length ?? 0;
      } else {
        movedToParentId = targetItem.parentKey?.toString() ?? null;
        const siblings = movedToParentId ? (articlesTree.getItem(movedToParentId)?.children ?? []) : articlesTree.items;
        const targetIndex = siblings.findIndex((i) => i.key === e.target.key);
        movedToIndex = e.target.dropPosition === 'before' ? targetIndex : targetIndex + 1;
      }

      const moveDetails = {
        id: movedArticleId.toString(),
        parentId: movedToParentId,
        index: movedToIndex,
      };

      if (canMoveArticle && !canMoveArticle(moveDetails)) {
        return;
      }

      const movedItem = articlesTree.getItem(movedArticleId);
      const originalParentId = movedItem?.parentKey?.toString() ?? null;
      const originalSiblings = originalParentId
        ? (articlesTree.getItem(originalParentId)?.children ?? [])
        : articlesTree.items;
      const originalIndex = originalSiblings.findIndex((i) => i.key === movedArticleId);

      if (e.target.dropPosition === 'on') {
        articlesTree.move(movedArticleId, movedToParentId, movedToIndex);
      } else if (e.target.dropPosition === 'before') {
        articlesTree.moveBefore(e.target.key, e.keys);
      } else {
        articlesTree.moveAfter(e.target.key, e.keys);
      }

      const result = onMoveArticle?.(moveDetails);

      if (result && typeof result.then === 'function') {
        result.catch(() => {
          articlesTree.move(movedArticleId, originalParentId, originalIndex);
        });
      }
    },
    renderDropIndicator: (target) => (
      <DropIndicator
        target={target}
        className="outline outline-1 outline-lime-200 ml-[calc(var(--tree-item-level)*0.5rem)]"
      />
    ),
  });

  // Selection when there's no active article from URL/context; used so the tree can show a selection before navigation.
  const [localSelectedKeys, setLocalSelectedKeys] = React.useState<Set<Key>>(new Set());
  const selectedKeys = React.useMemo(
    () => (effectiveActiveId != null ? new Set<Key>([effectiveActiveId]) : localSelectedKeys),
    [effectiveActiveId, localSelectedKeys],
  );

  const pathToActive = React.useMemo(() => {
    if (!effectiveActiveId || isSearching || !articles.length) return [];
    const path = getParentKeys(articles, effectiveActiveId);

    return path ?? [];
  }, [articles, effectiveActiveId, isSearching]);

  const [expandedKeysState, setExpandedKeysState] = React.useState<Set<Key>>(new Set());
  const expandedKeys = React.useMemo(
    () => new Set<Key>([...pathToActive, ...expandedKeysState]),
    [pathToActive, expandedKeysState],
  );

  React.useEffect(() => {
    if (!activeArticleId) return;
    setLastSelectedArticleId(null);
  }, [activeArticleId, lastSelectedArticleId, setLastSelectedArticleId]);

  const pendingAddIdsRef = React.useRef<Set<string>>(new Set());
  const [pendingAddIds, setPendingAddIds] = React.useState<Set<string>>(new Set());

  const handleAddArticle = React.useCallback(
    (parentId: string) => {
      if (isSearching || !onAddArticle || pendingAddIdsRef.current.has(parentId)) return;

      pendingAddIdsRef.current.add(parentId);
      setPendingAddIds(new Set(pendingAddIdsRef.current));

      const tempId = `__placeholder_${++placeholderCounter}`;
      const placeholder: ArticleNavItem = { id: tempId, label: 'Untitled', href: '', status: 'draft' };

      articlesTree.append(parentId, placeholder);
      setExpandedKeysState((prev) => new Set([...prev, parentId]));

      onAddArticle(parentId).then(
        (created) => {
          articlesTree.remove(tempId);
          articlesTree.append(parentId, created);
          // Highlight the newly created article in the tree.
          setLocalSelectedKeys(new Set([created.id]));
          onSelectArticle?.(created.id);
          pendingAddIdsRef.current.delete(parentId);
          setPendingAddIds(new Set(pendingAddIdsRef.current));
        },
        () => {
          articlesTree.remove(tempId);
          pendingAddIdsRef.current.delete(parentId);
          setPendingAddIds(new Set(pendingAddIdsRef.current));
        },
      );
    },
    [isSearching, onAddArticle, articlesTree, onSelectArticle],
  );

  const navigateToArticleId = React.useCallback(
    (articleId: string) => {
      onSelectArticle?.(articleId);
      // No URL/store id: keep highlight in sync for Storybook and other uncontrolled hosts.
      if (activeArticleId == null) {
        setLocalSelectedKeys(new Set<Key>([articleId]));
      }
      if (isSearching) {
        setLastSelectedArticleId(articleId);
        setSearchValue('');
        handleSearchArticles?.('');
      }
    },
    [activeArticleId, handleSearchArticles, isSearching, onSelectArticle, setLastSelectedArticleId, setSearchValue],
  );

  return (
    <Tree
      aria-label={isSearching ? 'Search results' : 'Articles tree'}
      items={articlesTree.items}
      renderEmptyState={renderEmptyState}
      dragAndDropHooks={isSearching ? undefined : dragAndDropHooks}
      className="flex flex-col"
      // toggle → selectOnFocus off in React Aria, so ArrowUp/Down only move focus; Space updates selection → onSelectionChange.
      selectionBehavior="toggle"
      selectionMode="single"
      expandedKeys={expandedKeys}
      onExpandedChange={(nextExpandedKeys) => {
        const newlyExpandedKeys = getNewlyExpandedKeys(expandedKeys, nextExpandedKeys);

        newlyExpandedKeys.forEach((key) => {
          onExpandArticle?.(key);
        });

        setExpandedKeysState(nextExpandedKeys);
      }}
      selectedKeys={selectedKeys}
      disallowEmptySelection
      onSelectionChange={(selection) => {
        if (selection !== 'all') {
          // Keep local selection in sync when we're not driven by URL/active article.
          if (effectiveActiveId == null) setLocalSelectedKeys(selection);
          const selected = Array.from(selection)?.[0];

          if (selected) {
            navigateToArticleId(selected.toString());
          }
        }
      }}
    >
      {function renderItem(item) {
        return (
          <TreeItem
            className="list-none"
            textValue={item.value.label}
            id={item.value.id}
            hasChildItems={item.value.hasChildren}
          >
            <TreeItemContent>
              {(props) => (
                <ArticleItem
                  ItemLinkComponent={ItemLinkComponent}
                  item={item}
                  onAddArticle={!isSearching && onAddArticle ? handleAddArticle : undefined}
                  isAddingArticle={!isSearching && pendingAddIds.has(item.value.id)}
                  isLoadingChildren={!isSearching && item.value.isLoadingChildren}
                  onMoreActions={onMoreActions ? (articleId) => onMoreActions(articleId) : undefined}
                  {...props}
                />
              )}
            </TreeItemContent>

            {!isSearching && item.children && <Collection items={item.children}>{renderItem}</Collection>}
          </TreeItem>
        );
      }}
    </Tree>
  );
}
